import { useState } from 'react';
import { api, ApiError } from '../../lib/api';
import type { OperatingHours, ParkingZone, RateIncrement, Tariff } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, Field, InfoNote, PageHeader, Spinner, StatusBadge } from '../../ui/components';

const WEEKDAYS: Array<{ key: keyof OperatingHours; label: string }> = [
  { key: 'mon', label: 'Monday' },
  { key: 'tue', label: 'Tuesday' },
  { key: 'wed', label: 'Wednesday' },
  { key: 'thu', label: 'Thursday' },
  { key: 'fri', label: 'Friday' },
  { key: 'sat', label: 'Saturday' },
  { key: 'sun', label: 'Sunday' },
];

export function ZonesAdmin() {
  const zonesQ = useApiQuery<{ items: ParkingZone[] }>('/api/v1/parking/zones');
  const [selectedZoneId, setSelectedZoneId] = useState('');

  return (
    <div>
      <PageHeader title="Zones & Tariffs" subtitle="Register parking zones and publish tariff versions (PARKING_ADMIN only)." />

      <div className="mb-6 card overflow-x-auto">
        {zonesQ.loading ? (
          <Spinner />
        ) : zonesQ.error ? (
          <div className="p-4">
            <ErrorNote message={zonesQ.error} />
          </div>
        ) : (zonesQ.data?.items.length ?? 0) === 0 ? (
          <div className="p-4 text-sm text-slate-500">No zones yet — create one below.</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Code</th>
                <th className="table-head">Name</th>
                <th className="table-head">Capacity</th>
                <th className="table-head">Max stay</th>
                <th className="table-head">No-return</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {(zonesQ.data?.items ?? []).map((z) => (
                <tr key={z.id} className={`hover:bg-slate-50 ${z.id === selectedZoneId ? 'bg-brand-50' : ''}`}>
                  <td className="table-cell">
                    <button className="font-mono text-xs font-semibold text-brand-600 hover:underline" onClick={() => setSelectedZoneId(z.id)}>
                      {z.code}
                    </button>
                  </td>
                  <td className="table-cell text-xs">{z.name}</td>
                  <td className="table-cell text-xs">{z.capacity ?? '—'}</td>
                  <td className="table-cell text-xs">{z.maxStayMinutes ? `${z.maxStayMinutes} min` : '—'}</td>
                  <td className="table-cell text-xs">{z.noReturnMinutes ? `${z.noReturnMinutes} min` : '—'}</td>
                  <td className="table-cell">
                    <StatusBadge status={z.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <CreateZoneForm onCreated={() => zonesQ.reload()} />
        <CreateTariffForm zones={zonesQ.data?.items ?? []} selectedZoneId={selectedZoneId} onSelectZone={setSelectedZoneId} />
      </div>
    </div>
  );
}

function CreateZoneForm({ onCreated }: { onCreated: () => void }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [capacity, setCapacity] = useState('');
  const [maxStayMinutes, setMaxStayMinutes] = useState('');
  const [noReturnMinutes, setNoReturnMinutes] = useState('');
  const [hours, setHours] = useState<Record<string, { open: string; close: string }>>(
    Object.fromEntries(WEEKDAYS.map((d) => [d.key, { open: '', close: '' }])),
  );
  const [geometryText, setGeometryText] = useState('');
  const [centroidText, setCentroidText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<ParkingZone | null>(null);

  const submit = async () => {
    setError(null);
    if (!code.trim() || !name.trim()) {
      setError('Code and name are required.');
      return;
    }
    let coordinates: unknown;
    let centroidCoordinates: unknown;
    try {
      coordinates = JSON.parse(geometryText);
    } catch {
      setError('Polygon coordinates is not valid JSON.');
      return;
    }
    try {
      centroidCoordinates = JSON.parse(centroidText);
    } catch {
      setError('Centroid coordinates is not valid JSON.');
      return;
    }
    const operatingHours: OperatingHours = {};
    for (const d of WEEKDAYS) {
      const band = hours[d.key];
      operatingHours[d.key] = band && band.open && band.close ? [[band.open, band.close]] : [];
    }
    setBusy(true);
    try {
      const zone = await api.post<ParkingZone>('/api/v1/parking/zones', {
        code: code.trim(),
        name: name.trim(),
        description: description.trim() || undefined,
        geometry: { type: 'Polygon', coordinates },
        centroid: { type: 'Point', coordinates: centroidCoordinates },
        capacity: capacity.trim() ? Number(capacity) : undefined,
        operatingHours,
        maxStayMinutes: maxStayMinutes.trim() ? Number(maxStayMinutes) : undefined,
        noReturnMinutes: noReturnMinutes.trim() ? Number(noReturnMinutes) : undefined,
      });
      setCreated(zone);
      setCode('');
      setName('');
      setDescription('');
      setCapacity('');
      setMaxStayMinutes('');
      setNoReturnMinutes('');
      setGeometryText('');
      setCentroidText('');
      setHours(Object.fromEntries(WEEKDAYS.map((d) => [d.key, { open: '', close: '' }])));
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not create zone');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-5">
      <h2 className="mb-3 text-sm font-bold text-slate-700">Create zone</h2>
      {created ? <div className="mb-3"><InfoNote message={`Zone ${created.code} created.`} /></div> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Code">
            <input className="field-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. VIC-BEA-01" />
          </Field>
          <Field label="Name">
            <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Beau Vallon Beach Road" />
          </Field>
        </div>
        <Field label="Description" hint="Optional">
          <input className="field-input" value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Capacity" hint="Optional">
            <input className="field-input" inputMode="numeric" value={capacity} onChange={(e) => setCapacity(e.target.value)} />
          </Field>
          <Field label="Max stay (minutes)" hint="Optional">
            <input className="field-input" inputMode="numeric" value={maxStayMinutes} onChange={(e) => setMaxStayMinutes(e.target.value)} />
          </Field>
          <Field label="No-return (minutes)" hint="Optional">
            <input className="field-input" inputMode="numeric" value={noReturnMinutes} onChange={(e) => setNoReturnMinutes(e.target.value)} />
          </Field>
        </div>

        <div>
          <div className="field-label">Operating hours</div>
          <div className="space-y-1.5">
            {WEEKDAYS.map((d) => (
              <div key={d.key} className="grid grid-cols-[5rem_1fr_1fr] items-center gap-2">
                <span className="text-xs text-slate-500">{d.label}</span>
                <input
                  type="time"
                  className="field-input"
                  value={hours[d.key]?.open ?? ''}
                  onChange={(e) => setHours((prev) => ({ ...prev, [d.key]: { open: e.target.value, close: prev[d.key]?.close ?? '' } }))}
                />
                <input
                  type="time"
                  className="field-input"
                  value={hours[d.key]?.close ?? ''}
                  onChange={(e) => setHours((prev) => ({ ...prev, [d.key]: { open: prev[d.key]?.open ?? '', close: e.target.value } }))}
                />
              </div>
            ))}
          </div>
          <p className="mt-1 text-[11px] text-slate-400">Leave both times blank for a day the zone is closed all day (e.g. Sunday).</p>
        </div>

        <Field label="Geometry — Paste Polygon GeoJSON coordinates" hint='e.g. [[[55.4505,-4.6205],[55.4545,-4.6205],[55.4545,-4.6185],[55.4505,-4.6185],[55.4505,-4.6205]]]'>
          <textarea className="field-input min-h-[4rem] font-mono text-xs" value={geometryText} onChange={(e) => setGeometryText(e.target.value)} />
        </Field>
        <Field label="Centroid — Paste Point GeoJSON coordinates" hint="e.g. [55.4525,-4.6195]">
          <textarea className="field-input min-h-[2.5rem] font-mono text-xs" value={centroidText} onChange={(e) => setCentroidText(e.target.value)} />
        </Field>

        <button className="btn-primary w-full" onClick={submit} disabled={busy || !code.trim() || !name.trim() || !geometryText.trim() || !centroidText.trim()}>
          {busy ? 'Creating…' : 'Create zone'}
        </button>
      </div>
    </div>
  );
}

function CreateTariffForm({
  zones,
  selectedZoneId,
  onSelectZone,
}: {
  zones: ParkingZone[];
  selectedZoneId: string;
  onSelectZone: (id: string) => void;
}) {
  const [version, setVersion] = useState('1');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [effectiveTo, setEffectiveTo] = useState('');
  const [statutoryReference, setStatutoryReference] = useState('');
  const [increments, setIncrements] = useState<RateIncrement[]>([{ minutes: 30, amountCents: 500 }]);
  const [halfDayAmountCents, setHalfDayAmountCents] = useState('');
  const [fullDayAmountCents, setFullDayAmountCents] = useState('');
  const [monthlyPermitAmountCents, setMonthlyPermitAmountCents] = useState('');
  const [freeGraceMinutes, setFreeGraceMinutes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<Tariff | null>(null);

  const updateIncrement = (i: number, field: keyof RateIncrement, value: number) => {
    setIncrements((prev) => prev.map((inc, idx) => (idx === i ? { ...inc, [field]: value } : inc)));
  };

  const submit = async () => {
    setError(null);
    if (!selectedZoneId) {
      setError('Choose a zone from the list above first.');
      return;
    }
    if (!statutoryReference.trim() || !effectiveFrom) {
      setError('Statutory reference and effective-from date are required.');
      return;
    }
    setBusy(true);
    try {
      const tariff = await api.post<Tariff>(`/api/v1/parking/zones/${selectedZoneId}/tariffs`, {
        version: Number(version),
        effectiveFrom: new Date(effectiveFrom).toISOString(),
        effectiveTo: effectiveTo ? new Date(effectiveTo).toISOString() : undefined,
        statutoryReference: statutoryReference.trim(),
        rateStructure: {
          increments,
          halfDayAmountCents: halfDayAmountCents.trim() ? Number(halfDayAmountCents) : undefined,
          fullDayAmountCents: fullDayAmountCents.trim() ? Number(fullDayAmountCents) : undefined,
          monthlyPermitAmountCents: monthlyPermitAmountCents.trim() ? Number(monthlyPermitAmountCents) : undefined,
          freeGraceMinutes: freeGraceMinutes.trim() ? Number(freeGraceMinutes) : undefined,
        },
      });
      setCreated(tariff);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not create tariff version');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-5">
      <h2 className="mb-3 text-sm font-bold text-slate-700">Create tariff version</h2>
      <Field label="Zone" className="mb-3">
        <select className="field-input" value={selectedZoneId} onChange={(e) => onSelectZone(e.target.value)}>
          <option value="">Choose a zone…</option>
          {zones.map((z) => (
            <option key={z.id} value={z.id}>
              {z.code} — {z.name}
            </option>
          ))}
        </select>
      </Field>

      {created ? (
        <div className="mb-3">
          <InfoNote message={`Tariff version ${created.version} created for this zone.`} />
        </div>
      ) : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}

      <div className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Version">
            <input className="field-input" inputMode="numeric" value={version} onChange={(e) => setVersion(e.target.value)} />
          </Field>
          <Field label="Effective from">
            <input type="date" className="field-input" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
          </Field>
          <Field label="Effective to" hint="Optional">
            <input type="date" className="field-input" value={effectiveTo} onChange={(e) => setEffectiveTo(e.target.value)} />
          </Field>
        </div>
        <Field label="Statutory reference">
          <input className="field-input" value={statutoryReference} onChange={(e) => setStatutoryReference(e.target.value)} placeholder="e.g. S.I. 12 of 2026" />
        </Field>

        <div>
          <div className="field-label">Rate increments</div>
          <div className="space-y-2">
            {increments.map((inc, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_auto] items-center gap-2">
                <input
                  type="number"
                  className="field-input"
                  value={inc.minutes}
                  onChange={(e) => updateIncrement(i, 'minutes', Number(e.target.value))}
                  placeholder="Minutes"
                />
                <input
                  type="number"
                  className="field-input"
                  value={inc.amountCents}
                  onChange={(e) => updateIncrement(i, 'amountCents', Number(e.target.value))}
                  placeholder="Amount (cents)"
                />
                <button
                  className="btn-secondary !py-1.5"
                  onClick={() => setIncrements((prev) => prev.filter((_, idx) => idx !== i))}
                  disabled={increments.length <= 1}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
          <button className="btn-secondary mt-2" onClick={() => setIncrements((prev) => [...prev, { minutes: 60, amountCents: 0 }])}>
            + Add increment
          </button>
          <p className="mt-1 text-[11px] text-slate-400">Each row: after N minutes, the session costs N cents (SCR × 100).</p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Half-day amount (cents)" hint="Optional">
            <input className="field-input" inputMode="numeric" value={halfDayAmountCents} onChange={(e) => setHalfDayAmountCents(e.target.value)} />
          </Field>
          <Field label="Full-day amount (cents)" hint="Optional">
            <input className="field-input" inputMode="numeric" value={fullDayAmountCents} onChange={(e) => setFullDayAmountCents(e.target.value)} />
          </Field>
          <Field label="Monthly permit amount (cents)" hint="Optional">
            <input className="field-input" inputMode="numeric" value={monthlyPermitAmountCents} onChange={(e) => setMonthlyPermitAmountCents(e.target.value)} />
          </Field>
          <Field label="Free grace (minutes)" hint="Optional">
            <input className="field-input" inputMode="numeric" value={freeGraceMinutes} onChange={(e) => setFreeGraceMinutes(e.target.value)} />
          </Field>
        </div>

        <button className="btn-primary w-full" onClick={submit} disabled={busy || !selectedZoneId}>
          {busy ? 'Creating…' : 'Create tariff version'}
        </button>
      </div>
    </div>
  );
}
